import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { CustomValidatorsService } from '@app/@shared/custom-validators/custom-validators.service';
import { ResponseDialogComponent } from '@app/@shared/dialogs/response/response-dialog.component';
import { LoaderGenericService } from '@app/@shared/loader-generic/loader-generic.service';
import { CombosStore } from '@app/requests/combos-service/combos.store';
import { CommonDataDTO } from '@app/requests/model/combos-model';
import { RequestComboReports } from '@app/users/model/users-model';
import { AttachDocumentsDTO, CertificatePaymentDTO, UserByCaseRequestDTO } from '../model/payment-model';
import { PaymentCertApi } from '../services/paymentCert.service';
import { TranslateService } from '@ngx-translate/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Credentials } from '@app/@core/models/login-models';
import { COMMERCIAL_EXTERNAL, ESTABLISHMENT, PRESCRIBER, VENDOR } from '@app/@core/constants/users.constants';
import { CredentialsService } from '@app/@core/auth/credentials.service';

const FORM_PARAM_CHAIN = 'chain';
const FORM_PARAM_ESTABLISHMENT = 'establishment';
const FORM_PARAM_PRESCRIPTOR = 'prescriptor';

const SUCCESSFULL_CODE_REGISTER_PAYMENT = '000';

@Component({
  selector: 'app-register-payment',
  templateUrl: './register-payment.component.html',
  styleUrls: ['./register-payment.component.scss'],
})
export class RegisterPaymentComponent implements OnInit {
  @Output() establishment = new EventEmitter<CommonDataDTO>();
  @Output() chain = new EventEmitter<CommonDataDTO>();
  @Output() prescriptor = new EventEmitter<CommonDataDTO>();

  readonly LimitOfFiles = 1;
  fileToSend: AttachDocumentsDTO[] = [];

  registerPayment: FormGroup;

  macroChainList: CommonDataDTO[] = [];
  macroOfficeList: CommonDataDTO[] = [];
  chainsList: CommonDataDTO[];
  prescriptorsList: CommonDataDTO[];
  establishmentsList: CommonDataDTO[];
  resetFile: boolean = false;
  isEnableFile: boolean = true;
  private userInfo: Credentials;
  isExternalCommercial: boolean;

  constructor(
    readonly formBuilder: FormBuilder,
    readonly loaderGenericService: LoaderGenericService,
    readonly combosStore: CombosStore,
    readonly dialog: MatDialog,
    readonly customValidators: CustomValidatorsService,
    readonly paymentServices: PaymentCertApi,
    readonly translateService: TranslateService,
    readonly credentialsService: CredentialsService
  ) {
    this.userInfo = this.credentialsService.credentials;
  }

  ngOnInit(): void {
    this.registerPayment = this.formBuilder.group({
      chain: [{ value: '', disabled: false }, Validators.required],
      prescriptor: [{ value: '', disabled: true }, Validators.required],
      establishment: [{ value: '', disabled: true }, Validators.required],
      amountRequested: [
        { value: '', disabled: true },
        [Validators.required, this.maxValueValidator(999999999999), this.minValueValidator(0)],
      ],
      numSol: [{ value: '', disabled: true }, [Validators.required, this.customValidators.isSolValid]],
    });
    this.isExternalCommercial = this.userInfo.userType === COMMERCIAL_EXTERNAL;
    if (this.isExternalCommercial) {
      this.loadEstablishmentFromCommercialExternal();
      this.registerPayment = this.formBuilder.group({
        establishment: [{ value: '', disabled: false }, Validators.required],
        amountRequested: [
          { value: '', disabled: true },
          [Validators.required, this.maxValueValidator(999999999999), this.minValueValidator(0)],
        ],
        numSol: [{ value: '', disabled: true }, [Validators.required, this.customValidators.isSolValid]],
      });
    } else {
      this.loadChainsCombo();
    }
  }

  maxValueValidator(maxValue: number) {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value !== null && control.value !== '' && +control.value > maxValue) {
        return { maxValue: { requiredValue: maxValue, actualValue: control.value } };
      }
      return null;
    };
  }

  minValueValidator(minValue: number) {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value !== null && control.value !== '' && +control.value <= minValue) {
        return { maxValue: { requiredValue: minValue, actualValue: control.value } };
      }
      return null;
    };
  }

  readonly setValueSelect = (formControlName: string, array: CommonDataDTO[]) => {
    if (array?.length > 0 && array.length === 1) {
      this.registerPayment.controls[formControlName]?.setValue(array[0]?.id);
      if (formControlName === FORM_PARAM_ESTABLISHMENT) {
        this.establishment.emit(this.registerPayment.controls.establishment.value);
        this.registerPayment.get('amountRequested').enable();
        this.registerPayment.get('numSol').enable();
      } else if (formControlName === FORM_PARAM_CHAIN) {
        this.chain.emit(this.registerPayment.controls.chain.value);
        this.registerPayment.get('prescriptor').enable();
        this.loadPrescribersByChains();
      } else if (formControlName === FORM_PARAM_PRESCRIPTOR) {
        this.prescriptor.emit(this.registerPayment.controls.prescriptor.value);
        this.registerPayment.get('establishment').enable();
        this.loadEstablishmentComboByPrescribers();
      }
    }
  };

  readonly loadChainsCombo = async (filter?: UserByCaseRequestDTO) => {
    this.loaderGenericService.showSpinner();
    this.resetFormControlAndDataList(FORM_PARAM_CHAIN);
    this.resetFormControlAndDataList(FORM_PARAM_PRESCRIPTOR);
    this.resetFormControlAndDataList(FORM_PARAM_ESTABLISHMENT);
    this.combosStore
      .getChains(filter)
      .then(
        (result) => {
          if (result?.length > 0) {
            this.chainsList = result;
            this.setValueSelect(FORM_PARAM_CHAIN, this.chainsList);
          }
        },
        (error) => console.error(error)
      )
      .catch((error) => console.error(error))
      .finally(() => {
        this.loaderGenericService.stopSpinner();
      });
  };

  readonly loadPrescribersByChains = async () => {
    this.loaderGenericService.showSpinner();
    this.resetFormControlAndDataList(FORM_PARAM_PRESCRIPTOR);
    this.resetFormControlAndDataList(FORM_PARAM_ESTABLISHMENT);
    this.registerPayment.get('prescriptor').enable();

    const request = {
      listChains: [this.registerPayment.controls[FORM_PARAM_CHAIN].value],
    };
    if (request) {
      if (
        this.userInfo.userType === VENDOR ||
        this.userInfo.userType === ESTABLISHMENT ||
        this.userInfo.userType === PRESCRIBER
      ) {
        this.combosStore.getPrescribers({}).then(
          (result) => {
            if (result?.length > 0) {
              this.prescriptorsList = result;
              this.setValueSelect(FORM_PARAM_PRESCRIPTOR, this.prescriptorsList);
              this.loaderGenericService.stopSpinner();
            }
          },
          (error) => console.error(error)
        );
      } else {
        this.combosStore.getPrescribersByChains(request).then(
          (result) => {
            if (result?.length > 0) {
              this.prescriptorsList = result;
              this.setValueSelect(FORM_PARAM_PRESCRIPTOR, this.prescriptorsList);
              this.loaderGenericService.stopSpinner();
            }
          },
          (error) => console.error(error)
        );
      }
    }
  };

  readonly loadEstablishmentComboByPrescribers = async () => {
    this.resetFormControlAndDataList(FORM_PARAM_ESTABLISHMENT);
    this.registerPayment.get('establishment').enable();
    const request: RequestComboReports = {
      //El dto contiene arrays
      listPrescribers: [this.registerPayment.controls.prescriptor?.value],
      listChains: [this.registerPayment.controls.chain?.value],
      officeId: this.registerPayment.controls.macroOffice?.value,
    };

    if (request) {
      if (this.userInfo.userType === VENDOR || this.userInfo.userType === ESTABLISHMENT) {
        this.combosStore.getEstablishments({}).then(
          (result) => {
            if (result?.length > 0) {
              this.establishmentsList = result;
              this.setValueSelect(FORM_PARAM_ESTABLISHMENT, this.establishmentsList);
              this.loaderGenericService.stopSpinner();
            }
          },
          (error) => console.error(error)
        );
      } else {
        this.combosStore
          .getEstablishmentsByPrescribers(request)
          .then(
            (result) => {
              if (result?.length > 0) {
                this.establishmentsList = result;
                this.setValueSelect(FORM_PARAM_ESTABLISHMENT, this.establishmentsList);
              }
            },
            (error) => console.log(error)
          )
          .catch((error) => console.log(error));
      }
    }
  };

  readonly loadEstablishmentFromCommercialExternal = async () => {
    let commercialDto = {
      commercialId: this.userInfo.userId,
    };
    this.combosStore.getEstablishmentWithCertifiedPaymentByExternalCommercial(commercialDto).then((result) => {
      if (result?.length > 0) {
        this.establishmentsList = result;
        this.setValueSelect(FORM_PARAM_ESTABLISHMENT, this.establishmentsList);
      }
    });
    this.registerPayment.get('establishment').enable();
  };

  readonly resetFormControlAndDataList = (formControlName: string) => {
    const formControl = this.registerPayment.controls[formControlName];
    if (formControl) {
      formControl.reset();
      switch (formControlName) {
        case FORM_PARAM_CHAIN:
          this.chainsList = [];
          break;
        case FORM_PARAM_PRESCRIPTOR:
          this.prescriptorsList = [];
          this.registerPayment.get('prescriptor').disable();
          break;
        case FORM_PARAM_ESTABLISHMENT:
          this.establishmentsList = [];
          this.registerPayment.get('establishment').disable();

          this.registerPayment.controls.amountRequested.setValue('');
          this.registerPayment.get('amountRequested').disable();

          this.registerPayment.controls.numSol.setValue('');
          this.registerPayment.get('numSol').disable();
          break;
        default:
          console.error('formControlName - NOT FOUND', formControlName);
      }
    }
  };

  private showDialogError(error: HttpErrorResponse) {
    console.error(error);
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '90%';
    dialogConfig.maxWidth = '450px';
    dialogConfig.data = { text: error?.error?.message, icon: 'icon-ic_circle_cross' };
    this.dialog.open(ResponseDialogComponent, dialogConfig);
  }

  private showDialogWithControledError(error: string) {
    console.error(error);
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '90%';
    dialogConfig.maxWidth = '450px';
    dialogConfig.data = { text: error, icon: 'icon-ic_circle_cross' };
    this.dialog.open(ResponseDialogComponent, dialogConfig);
  }

  private showDialogSuccess() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '90%';
    dialogConfig.maxWidth = '450px';
    dialogConfig.data = {
      text: this.translateService.instant('paymentCert.registerPayment.successful'),
      icon: 'icon-ic_circle_check',
      noRedirect: true,
    };
    const dialogRef = this.dialog.open(ResponseDialogComponent, dialogConfig);

    dialogRef.afterClosed().subscribe((_result) => {
      this.resetForm();
    });
  }

  transformAmount(value: Event, element: HTMLElement) {
    const inputForm = typeof element === 'string' ? element : element?.getAttribute('formControlName');

    if (this.registerPayment.controls[inputForm].valid) {
      let inputValue = (value.target as HTMLInputElement).value;

      if (inputValue.slice(-1) === '€') {
        //Si value entra formateado, quitamos formateo
        inputValue = inputValue.replace(/\ /g, '');
        inputValue = inputValue.replace(/\€/g, '');
        inputValue = inputValue.replace(/\./g, '');
      }
      this.registerPayment.controls[inputForm].setValue(this.customValidators.transformAmount(inputValue));
    }
  }

  numberOnly(event: KeyboardEvent | ClipboardEvent, element: HTMLInputElement): boolean {
    if (event instanceof KeyboardEvent && event.type === 'keypress') {
      const inputForm = typeof element === 'string' ? element : element?.getAttribute('formControlName');
      const value = this.registerPayment.controls[inputForm].value;
      return this.customValidators.numberOnly(event.key.charCodeAt(0), value);
    } else if (event instanceof ClipboardEvent && event.type === 'paste') {
      const value = event.clipboardData?.getData('text') || '';
      for (const char of value) {
        if (!this.customValidators.numberOnly(char.charCodeAt(0), char)) {
          return false;
        }
      }
    }
  }

  onChainSelection(event: HTMLSelectElement) {
    this.registerPayment.controls[FORM_PARAM_CHAIN].setValue(event.value);
    this.loadPrescribersByChains();
  }

  onPrescriberSelection(event: HTMLSelectElement) {
    this.registerPayment.controls[FORM_PARAM_PRESCRIPTOR].setValue(event.value);
    this.loadEstablishmentComboByPrescribers();
  }

  onEstablishmentSelection(event: HTMLSelectElement) {
    this.registerPayment.controls[FORM_PARAM_ESTABLISHMENT].setValue(event.value);
    this.registerPayment.get('amountRequested').enable();
    this.registerPayment.get('numSol').enable();
  }

  formatAmountWithoutSimbolToSend(currency: string): string {
    currency = currency.replace('€', '').trim();
    currency = currency.replace(/\./g, '');

    return currency;
  }

  resetForm() {
    this.registerPayment.reset();
    if (!this.isExternalCommercial) {
      this.loadChainsCombo();
    } else {
      this.loadEstablishmentFromCommercialExternal();
    }
    this.isEnableFile = true;
    //desabilitamos todos los campos
    this.registerPayment.get('amountRequested').disable();
    this.registerPayment.get('numSol').disable();
    if (!this.isExternalCommercial) {
      this.registerPayment.get('prescriptor').disable();
      this.registerPayment.get('establishment').disable();
    }
  }

  private sendRegisterPayment(request: CertificatePaymentDTO) {
    this.loaderGenericService.showSpinner();
    this.paymentServices
      .registerPaymentCert(request)
      .then(
        (data) => {
          this.loaderGenericService.stopSpinner();
          if (data.id === SUCCESSFULL_CODE_REGISTER_PAYMENT) {
            this.showDialogSuccess();
            this.resetFile = true;
            this.fileToSend = [];
            this.isEnableFile = true;
          } else {
            this.showDialogWithControledError(data.value);
          }
        },
        (error) => {
          this.loaderGenericService.stopSpinner();
          this.showDialogError(error);
          console.error(error);
        }
      )
      .catch((error) => {
        this.loaderGenericService.stopSpinner();
        this.showDialogError(error);
        console.error(error);
      });
  }

  getFileAsBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        const base64String = reader.result as string;
        resolve(base64String);
      };

      reader.onerror = (error) => {
        reject(error);
      };

      reader.readAsDataURL(file);
    });
  }

  setFilesToUpload(event: Event) {
    this.resetFile = false;
    if (event[0] != null) {
      const file = event[0] as File;
      this.getFileAsBase64(file)
        .then((base64String) => {
          const fileUpload: AttachDocumentsDTO = {
            fileName: file.name,
            description: '',
            base64: base64String.split(',')[1],
            type: file.type.split('/')[1],
            extension: file.name.split('.')[1],
          };
          this.fileToSend.push(fileUpload);
          this.isEnableFile = false;
        })
        .catch((error) => {
          console.error('Error al convertir el archivo a Base64:', error);
        });
    } else {
      this.isEnableFile = true;
      this.fileToSend = [];
    }
  }

  onSendClick() {
    const requestToSend: CertificatePaymentDTO = {
      chainId: this.registerPayment.controls.chain?.value.slice(0, -1),
      prescriberId: this.registerPayment.controls.prescriptor?.value.slice(0, -1),
      establishmentId: this.registerPayment.controls.establishment.value.slice(0, -1),
      amount: this.formatAmountWithoutSimbolToSend(this.registerPayment.controls.amountRequested.value),
      numSol: this.registerPayment.controls.numSol.value,
      file: this.fileToSend[0],
    };

    this.sendRegisterPayment(requestToSend);
  }
}
